import { useCallback, useState, useEffect } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  Connection,
  Edge,
  Node,
  BackgroundVariant,
  Panel,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { TableNode } from './table-node';
import { JoinEdge } from './join-edge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RiInformationLine, RiDragMove2Line, RiLinkM, RiCheckboxCircleLine } from '@remixicon/react';
import { Button } from '@/components/ui/button';

const nodeTypes = {
  table: TableNode,
};

const edgeTypes = {
  join: JoinEdge,
};

interface VisualCanvasProps {
  tables: Array<{
    id: string;
    name: string;
    columns: Array<{
      name: string;
      type: string;
      isPrimaryKey?: boolean;
      isForeignKey?: boolean;
    }>;
  }>;
  onQueryChange: (sql: string) => void;
}

export function VisualCanvas({ tables, onQueryChange }: VisualCanvasProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedColumns, setSelectedColumns] = useState<Record<string, string[]>>({});

  const onConnect = useCallback(
    (params: Connection) => {
      const newEdge: Edge = {
        ...params,
        type: 'join',
        animated: true,
        data: {
          joinType: 'INNER',
        },
      };
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges]
  );

  const removeTableFromCanvas = (tableId: string) => {
    setNodes((nds) => nds.filter((n) => n.id !== tableId));
    // Also remove any edges connected to this table
    setEdges((eds) => eds.filter((e) => e.source !== tableId && e.target !== tableId));
    // Clear selected columns for this table
    setSelectedColumns((prev) => {
      const updated = { ...prev };
      delete updated[tableId];
      return updated;
    });
  };

  const addTableToCanvas = (table: any) => {
    const newNode: Node = {
      id: table.id,
      type: 'table',
      position: {
        x: Math.random() * 500,
        y: Math.random() * 300,
      },
      data: {
        label: table.name,
        columns: table.columns,
        onColumnSelect: (columnName: string) => {
          setSelectedColumns((prev) => {
            const tableColumns = prev[table.id] || [];
            const isSelected = tableColumns.includes(columnName);

            return {
              ...prev,
              [table.id]: isSelected
                ? tableColumns.filter((c) => c !== columnName)
                : [...tableColumns, columnName],
            };
          });
        },
        onRemove: () => removeTableFromCanvas(table.id),
        selectedColumns: selectedColumns[table.id] || [],
      },
    };

    setNodes((nds) => [...nds, newNode]);
  };

  const generateSQL = useCallback(() => {
    if (nodes.length === 0) {
      onQueryChange('');
      return;
    }

    // Generate SELECT clause
    const selectParts: string[] = [];
    nodes.forEach((node) => {
      const tableName = node.data.label;
      const columns = selectedColumns[node.id] || [];

      if (columns.length > 0) {
        columns.forEach((col) => {
          selectParts.push(`${tableName}.${col}`);
        });
      }
    });

    const selectClause = selectParts.length > 0
      ? selectParts.join(', ')
      : nodes.map((n) => `${n.data.label}.*`).join(', ');

    // Generate FROM clause
    const fromClause = nodes[0]?.data.label || '';

    // Generate JOIN clauses
    const joinClauses: string[] = [];
    edges.forEach((edge) => {
      const sourceNode = nodes.find((n) => n.id === edge.source);
      const targetNode = nodes.find((n) => n.id === edge.target);

      if (sourceNode && targetNode) {
        const joinType = edge.data?.joinType || 'INNER';
        joinClauses.push(
          `${joinType} JOIN ${targetNode.data.label} ON ${sourceNode.data.label}.id = ${targetNode.data.label}.${sourceNode.data.label}_id`
        );
      }
    });

    const sql = `SELECT ${selectClause}\nFROM ${fromClause}${
      joinClauses.length > 0 ? '\n' + joinClauses.join('\n') : ''
    };`;

    onQueryChange(sql);
  }, [nodes, edges, selectedColumns, onQueryChange]);

  // Update SQL whenever nodes, edges, or selectedColumns change
  useEffect(() => {
    generateSQL();
  }, [generateSQL]);

  // Update node data when selectedColumns change
  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        data: {
          ...node.data,
          selectedColumns: selectedColumns[node.id] || [],
          onRemove: () => removeTableFromCanvas(node.id),
        },
      }))
    );
  }, [selectedColumns, setNodes]);

  return (
    <div className="h-full w-full flex gap-4">
      {/* Table Picker Sidebar */}
      <Card className="w-72 flex flex-col">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Tables</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Click to add tables to canvas
          </p>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto p-3 pt-0">
          {tables.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No tables found in database
            </div>
          ) : (
            <div className="space-y-1.5">
              {tables.map((table) => {
                const isAdded = nodes.some((n) => n.id === table.id);
                return (
                  <Button
                    key={table.id}
                    onClick={() => addTableToCanvas(table)}
                    disabled={isAdded}
                    variant={isAdded ? "secondary" : "outline"}
                    className="w-full justify-start text-sm h-9 transition-all"
                  >
                    <RiCheckboxCircleLine className={`h-3.5 w-3.5 mr-2 ${isAdded ? 'text-primary' : 'text-muted-foreground'}`} />
                    {table.name}
                  </Button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Canvas */}
      <Card className="flex-1 relative overflow-hidden">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
        >
          <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
          <Controls />
          <MiniMap
            nodeColor={(node) => '#6366f1'}
            maskColor="rgb(0, 0, 0, 0.1)"
            className="!bg-background !border !border-border"
          />

          {/* Info Panel */}
          {nodes.length === 0 && (
            <Panel position="top-center" className="bg-background/95 backdrop-blur border rounded-lg shadow-lg p-4 max-w-md">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <RiInformationLine className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 space-y-2">
                  <h4 className="font-semibold text-sm">How to build visual queries</h4>
                  <div className="space-y-1.5 text-xs text-muted-foreground">
                    <div className="flex items-start gap-2">
                      <RiCheckboxCircleLine className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>Click tables from the sidebar to add them to the canvas</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <RiLinkM className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>Drag from one table's handle to another to create joins</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <RiCheckboxCircleLine className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>Select columns by checking boxes in the table nodes</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <RiDragMove2Line className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>Drag tables to rearrange them on the canvas</span>
                    </div>
                  </div>
                </div>
              </div>
            </Panel>
          )}
        </ReactFlow>
      </Card>
    </div>
  );
}
